import React, { useState, useEffect } from 'react';
import {
  CircularProgress,
  useMediaQuery,
  useTheme,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Warning, ErrorOutline, AccessTime, HelpOutline } from '@mui/icons-material';

const formatTime = (seconds) => {
  if (!seconds || seconds <= 0 || isNaN(seconds)) return null;

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

const LoadingSpinner = () => (
  <CircularProgress size={20} style={{ color: '#20A8E0' }} />
);

// Calculate distance between two points (Haversine formula, in meters)
const calculateDistance = (p1, p2) => {
  if (!p1 || !p2 || !p1.latitude || !p1.longitude || !p2.lat || !p2.lng) return Infinity;
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (p1.latitude * Math.PI) / 180;
  const φ2 = (p2.lat * Math.PI) / 180;
  const Δφ = ((p2.lat - p1.latitude) * Math.PI) / 180;
  const Δλ = ((p2.lng - p1.longitude) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Load stop history from localStorage
const loadStopHistory = (vehicleId, scheduleDate) => {
  const key = `stopHistory_${vehicleId}_${scheduleDate}`;
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : {};
  } catch (error) {
    console.error('Error loading stop history:', error);
    return {};
  }
};

// Save stop history to localStorage
const saveStopHistory = (vehicleId, scheduleDate, history) => {
  const key = `stopHistory_${vehicleId}_${scheduleDate}`;
  try {
    localStorage.setItem(key, JSON.stringify(history));
  } catch (error) {
    console.error('Error saving stop history:', error);
  }
};

// Clear old history
const clearOldHistory = (vehicleId, currentScheduleDate) => {
  const prefix = `stopHistory_${vehicleId}_`;
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(prefix) && !key.endsWith(currentScheduleDate)) {
      localStorage.removeItem(key);
    }
  });
};

// Calculate stop status
const calculateStopStatus = (stop, vehiclePosition, stopSchedule, currentTime, isMoving, stoppedDuration) => {
  if (!stopSchedule) {
    return { status: 'No Schedule', tooltip: 'No schedule data available', icon: <HelpOutline style={{ color: 'gray' }} /> };
  }

  if (!vehiclePosition || !vehiclePosition.lat || !vehiclePosition.lng) {
    return {
      status: 'Unknown Location',
      tooltip: 'Vehicle location unavailable',
      icon: <HelpOutline style={{ color: 'gray' }} />,
    };
  }

  const stopPosition = { lat: parseFloat(stop.latitude), lng: parseFloat(stop.longitude) };
  const distance = calculateDistance(
    { latitude: vehiclePosition.lat, longitude: vehiclePosition.lng },
    stopPosition
  );
  const isNearStop = distance <= 100; // 100m radius
  const isStoppedAtStop = isNearStop && !isMoving && stoppedDuration >= 30; // Stopped for 30+ seconds

  // Parse arrivalTime (e.g., "04:53") and adjust to schedule date (assume IST)
  const [hours, minutes] = stopSchedule.arrivalTime.split(':').map(Number);
  const scheduleDate = new Date('2025-05-08'); // From vehicleSchedule.startTime
  const arrivalTime = new Date(scheduleDate);
  arrivalTime.setHours(hours, minutes, 0, 0);
  arrivalTime.setHours(arrivalTime.getHours() + 5, arrivalTime.getMinutes() + 30); // Adjust to IST (+05:30)

  const gracePeriodMs = 5 * 60 * 1000; // 5 minutes
  const timeDiffMs = currentTime - arrivalTime;

  if (timeDiffMs < 0) {
    // Future arrival
    return {
      status: 'Pending',
      tooltip: `Scheduled to arrive at ${stop.name} at ${arrivalTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`,
      icon: <AccessTime style={{ color: 'gray' }} />,
    };
  } else if (isStoppedAtStop && timeDiffMs <= gracePeriodMs) {
    // On time: Stopped within 100m, within 5min
    return {
      status: 'On Time',
      tooltip: `Stopped ${Math.round(distance)}m from ${stop.name} at ${new Date(currentTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`,
      icon: <CheckCircle style={{ color: 'green' }} />,
      distance,
      timestamp: currentTime,
    };
  } else if (isStoppedAtStop && timeDiffMs > gracePeriodMs) {
    // Delayed: Stopped within 100m, after 5min
    const delayMin = Math.floor(timeDiffMs / 60000);
    return {
      status: 'Delayed',
      tooltip: `Stopped ${Math.round(distance)}m from ${stop.name} ${delayMin} minutes late at ${new Date(currentTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`,
      icon: <Warning style={{ color: 'red' }} />,
      distance,
      timestamp: currentTime,
    };
  } else if (timeDiffMs > gracePeriodMs && !isStoppedAtStop) {
    // Missed: Past arrival time, not stopped within 100m
    return {
      status: 'Missed',
      tooltip: `Missed ${stop.name} scheduled at ${arrivalTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`,
      icon: <ErrorOutline style={{ color: 'orange' }} />,
      timestamp: currentTime,
    };
  }

  return {
    status: 'Unknown',
    tooltip: 'Unable to determine stop status',
    icon: <HelpOutline style={{ color: 'gray' }} />,
  };
};

const StopsTable = ({ stops, stopDetails, vehicleSchedule, vehiclePosition, isMoving, stoppedDuration, vehicleId }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [openModal, setOpenModal] = useState(false);
  const [selectedStop, setSelectedStop] = useState(null);
  const [stopHistory, setStopHistory] = useState({});

  const scheduleDate = '2025-05-08'; // From vehicleSchedule.startTime

  // Load stop history on mount
  useEffect(() => {
    if (vehicleId) {
      const history = loadStopHistory(vehicleId, scheduleDate);
      setStopHistory(history);
      clearOldHistory(vehicleId, scheduleDate);
    }
  }, [vehicleId, scheduleDate]);

  // Match stops with stopSchedules by stopName
  const stopSchedulesMap = vehicleSchedule?.[0]?.schedule?.stopSchedules?.reduce((acc, ss) => {
    acc[ss.stop.name] = ss;
    return acc;
  }, {}) || {};

  // Update stop history on status change
  useEffect(() => {
    if (!vehiclePosition || !vehicleId) return;

    const newHistory = { ...stopHistory };
    let hasChanges = false;

    sortedStops.forEach((stop) => {
      const stopSchedule = stopSchedulesMap[stop.name];
      const status = calculateStopStatus(
        stop,
        vehiclePosition,
        stopSchedule,
        Date.now(),
        isMoving,
        stoppedDuration
      );

      if (['On Time', 'Delayed', 'Missed'].includes(status.status)) {
        const currentEntry = newHistory[stop.name];
        if (
          !currentEntry ||
          currentEntry.status !== status.status ||
          currentEntry.timestamp !== status.timestamp
        ) {
          newHistory[stop.name] = {
            status: status.status,
            tooltip: status.tooltip,
            distance: status.distance,
            timestamp: status.timestamp,
          };
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      setStopHistory(newHistory);
      saveStopHistory(vehicleId, scheduleDate, newHistory);
    }
  }, [vehiclePosition, isMoving, stoppedDuration, vehicleId, scheduleDate]);

  // Group stops by name and keep the closest one
  const uniqueStops = stops.reduce((acc, stop) => {
    const distA = stopDetails[stop.stopId]?.distance;
    const numA = typeof distA === 'number' ? distA : parseFloat(distA);

    if (!acc[stop.name]) {
      acc[stop.name] = stop;
      return acc;
    }

    const existingDist = stopDetails[acc[stop.name].stopId]?.distance;
    const numExisting = typeof existingDist === 'number' ? existingDist : parseFloat(existingDist);

    if (!isNaN(numA) && (!numExisting || isNaN(numExisting) || numA < numExisting)) {
      acc[stop.name] = stop;
    }

    return acc;
  }, {});

  // Sort stops by distance
  const sortedStops = Object.values(uniqueStops).sort((a, b) => {
    const distA = stopDetails[a.stopId]?.distance;
    const distB = stopDetails[b.stopId]?.distance;
    const numA = typeof distA === 'number' ? distA : parseFloat(distA);
    const numB = typeof distB === 'number' ? distB : parseFloat(distB);

    if (isNaN(numA) && isNaN(numB)) return 0;
    if (isNaN(numA)) return 1;
    if (isNaN(numB)) return -1;
    return numA - numB;
  });

  const renderDistance = (details) => {
    if (!details?.distance || isNaN(parseFloat(details.distance))) {
      return <LoadingSpinner />;
    }
    return `${parseFloat(details.distance).toFixed(1)} km`;
  };

  const renderETA = (details) => {
    if (
      !details?.eta ||
      isNaN(details.eta) ||
      details.eta === 'NaN' ||
      details.eta === 0
    ) {
      return <LoadingSpinner />;
    }
    const formattedTime = formatTime(details.eta);
    return formattedTime || <LoadingSpinner />;
  };

  const renderStatus = (stop) => {
    const stopSchedule = stopSchedulesMap[stop.name];
    const status = calculateStopStatus(
      stop,
      vehiclePosition,
      stopSchedule,
      Date.now(),
      isMoving,
      stoppedDuration
    );
    return (
      <Tooltip title={status.tooltip}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          aria-label={status.tooltip}
          onClick={() => {
            setSelectedStop(stop);
            setOpenModal(true);
          }}
        >
          {status.icon}
          <span>{status.status}</span>
        </Box>
      </Tooltip>
    );
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedStop(null);
  };

  // Mobile card view
  if (isMobile) {
    return (
      <Box sx={{ width: '100%' }}>
        {sortedStops.map((stop, index) => {
          const details = stopDetails[stop.stopId] || { distance: 'N/A', eta: 'N/A' };
          return (
            <Box
              key={stop.stopId}
              sx={{
                padding: '16px',
                backgroundColor: index % 2 === 0 ? '#f2f2f2' : '#ffffff',
                borderBottom: '1px solid #ddd',
                borderRadius:
                  index === 0
                    ? '4px 4px 0 0'
                    : index === sortedStops.length - 1
                    ? '0 0 4px 4px'
                    : '0',
              }}
            >
              <Box sx={{ fontWeight: 600, fontSize: '16px', marginBottom: '8px' }}>
                {stop.name}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <Box sx={{ color: '#666', fontSize: '14px' }}>{t('Distance')}:</Box>
                <Box sx={{ fontSize: '14px' }}>{renderDistance(details)}</Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <Box sx={{ color: '#666', fontSize: '14px' }}>{t('ETA')}:</Box>
                <Box sx={{ fontSize: '14px' }}>{renderETA(details)}</Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ color: '#666', fontSize: '14px' }}>{t('Status')}:</Box>
                <Box sx={{ fontSize: '14px' }}>{renderStatus(stop)}</Box>
              </Box>
            </Box>
          );
        })}
        <Dialog open={openModal} onClose={handleCloseModal} aria-labelledby="stop-history-dialog">
          <DialogTitle id="stop-history-dialog">{t('Stop History')}</DialogTitle>
          <DialogContent>
            <List>
              {sortedStops.map((stop) => {
                const historyEntry = stopHistory[stop.name] || { status: 'Pending', tooltip: 'No history available' };
                return (
                  <ListItem key={stop.name}>
                    <ListItemIcon>
                      {historyEntry.status === 'On Time' && <CheckCircle style={{ color: 'green' }} />}
                      {historyEntry.status === 'Delayed' && <Warning style={{ color: 'red' }} />}
                      {historyEntry.status === 'Missed' && <ErrorOutline style={{ color: 'orange' }} />}
                      {historyEntry.status === 'Pending' && <AccessTime style={{ color: 'gray' }} />}
                      {historyEntry.status === 'Unknown' && <HelpOutline style={{ color: 'gray' }} />}
                    </ListItemIcon>
                    <ListItemText
                      primary={stop.name}
                      secondary={
                        <>
                          {historyEntry.tooltip}
                          {historyEntry.timestamp && (
                            <Typography variant="caption" display="block">
                              {new Date(historyEntry.timestamp).toLocaleString('en-IN')}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        overflowX: 'auto',
        borderRadius: '10px',
        display: 'flex',
        justifyContent: 'space-between',
        padding: '24px',
        backgroundColor: '#FFFFFF',
        border: '1px solid #A5DCF3',
        boxShadow: '-2px 1px 5px 1px #00000040',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: '500px' }}>
        <thead style={{ boxShadow: '-2px 2px 5px 0px #00000040', borderRadius: '8px' }}>
          <tr>
            <th
              style={{
                ...tableHeaderStyle,
                width: '10px',
                borderTopLeftRadius: '8px',
                borderBottomLeftRadius: '8px',
              }}
            ></th>
            <th style={tableHeaderStyle}>{t('Stops')}</th>
            <th style={tableHeaderStyle}>{t('Distance')}</th>
            <th style={tableHeaderStyle}>{t('ETA')}</th>
            <th style={{ ...tableHeaderStyle, borderTopRightRadius: '8px', borderBottomRightRadius: '8px' }}>
              {t('Status')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedStops.map((stop, index) => {
            const details = stopDetails[stop.stopId] || { distance: 'N/A', eta: 'N/A' };
            return (
              <tr key={stop.stopId}>
                <td style={tableCellStyle}></td>
                <td style={tableCellStyle}>{stop.name}</td>
                <td style={tableCellStyle}>{renderDistance(details)}</td>
                <td style={tableCellStyle}>{renderETA(details)}</td>
                <td style={tableCellStyle}>{renderStatus(stop)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Dialog open={openModal} onClose={handleCloseModal} aria-labelledby="stop-history-dialog">
        <DialogTitle id="stop-history-dialog">{t('Stop History')}</DialogTitle>
        <DialogContent>
          <List>
            {sortedStops.map((stop) => {
              const historyEntry = stopHistory[stop.name] || { status: 'Pending', tooltip: 'No history available' };
              return (
                <ListItem key={stop.name}>
                  <ListItemIcon>
                    {historyEntry.status === 'On Time' && <CheckCircle style={{ color: 'green' }} />}
                    {historyEntry.status === 'Delayed' && <Warning style={{ color: 'red' }} />}
                    {historyEntry.status === 'Missed' && <ErrorOutline style={{ color: 'orange' }} />}
                    {historyEntry.status === 'Pending' && <AccessTime style={{ color: 'gray' }} />}
                    {historyEntry.status === 'Unknown' && <HelpOutline style={{ color: 'gray' }} />}
                  </ListItemIcon>
                  <ListItemText
                    primary={stop.name}
                    secondary={
                      <>
                        {historyEntry.tooltip}
                        {historyEntry.timestamp && (
                          <Typography variant="caption" display="block">
                            {new Date(historyEntry.timestamp).toLocaleString('en-IN')}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const tableHeaderStyle = {
  backgroundColor: '#647490',
  color: 'white',
  fontSize: '16px',
  fontWeight: '600',
  textAlign: 'left',
  padding: '12px 20px',
  borderBottom: '2px solid #ddd',
};

const tableCellStyle = {
  padding: '12px 20px',
  textAlign: 'left',
  fontSize: '14px',
  borderBottom: '1px solid #ddd',
  color: '#333',
};

export default StopsTable;